import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	BanknoteArrowDown,
	Check,
	CircleCheck,
	Landmark,
	LoaderCircle,
	RotateCcw,
} from "lucide-react";
import { useEffect, useState } from "react";

import { api, type TransactionList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import {
	CHECK_DESTINATIONS,
	CheckDestinations,
	type CheckDestinationId,
} from "@/domain/check-destinations";
import { Checks } from "@/domain/checks";
import { FinancePresentation } from "@/presentation/finance";

type CheckKind = ReturnType<typeof Checks.list>[number]["kind"];
type CheckMovement = ReturnType<typeof Checks.list>[number];
type CheckFilter = "ALL" | CheckKind;

const checkKindPresentation: Record<
	CheckKind,
	{ label: string; className: string; icon: React.ReactNode }
> = {
	COMPENSATED: {
		label: "Cheque compensado",
		className: "border-emerald-200 bg-emerald-50 text-emerald-800",
		icon: <CircleCheck className="size-3.5" />,
	},
	CASH_WITHDRAWAL: {
		label: "Saque por cheque",
		className: "border-sky-200 bg-sky-50 text-sky-800",
		icon: <BanknoteArrowDown className="size-3.5" />,
	},
	RETURNED: {
		label: "Devolução de cheque",
		className: "border-red-200 bg-red-50 text-red-800",
		icon: <RotateCcw className="size-3.5" />,
	},
};

const checkFilters: CheckKind[] = ["COMPENSATED", "CASH_WITHDRAWAL", "RETURNED"];

export function ChecksView({ data }: { data: TransactionList }) {
	const queryClient = useQueryClient();
	const [filter, setFilter] = useState<CheckFilter>("ALL");
	const destinationMutation = useMutation({
		mutationFn: (input: {
			checkNumber: string;
			destinationId: CheckDestinationId;
			cashAccountId: "caixa-amanda" | "caixa-sindi" | null;
			explanation: string | null;
		}) => api.checks.setDestination(input),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["transactions", "current-month"] }),
				queryClient.invalidateQueries({ queryKey: ["treasury", "current-month"] }),
			]);
		},
	});
	const summary = Checks.summarize(data.transactions);
	const visibleMovements = summary.movements.filter(
		(movement) => filter === "ALL" || movement.kind === filter,
	);
	const groups = Checks.groupByDate(visibleMovements);

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
				<MetricCard
					label="Movimentado por cheque"
					value={FinancePresentation.money(summary.total.amountCents)}
					detail={`${summary.total.count} cheques e saques no mês`}
				/>
				<MetricCard
					label="Saques por cheque"
					value={FinancePresentation.money(summary.cashWithdrawals.amountCents)}
					detail={`${summary.cashWithdrawals.count} liquidações em dinheiro`}
				/>
				<MetricCard
					label="Transferido ao Caixa"
					value={FinancePresentation.money(summary.cashAccount.amountCents)}
					detail={`${summary.cashAccount.count} cheques para os caixas`}
					tone="success"
				/>
				<MetricCard
					label="Fretes de saída conciliados"
					value={FinancePresentation.money(summary.outboundFreight.amountCents)}
					detail={`${summary.outboundFreight.count} cheques CIF`}
					tone="success"
				/>
				<MetricCard
					label="Devoluções"
					value={FinancePresentation.money(summary.returned.amountCents)}
					detail={`${summary.returned.count} movimentos para revisar`}
					tone={summary.returned.count > 0 ? "attention" : "success"}
				/>
				<MetricCard
					label="Sem destino definido"
					value={FinancePresentation.money(summary.unassigned.amountCents)}
					detail={`${summary.unassigned.count} cheques e saques`}
					tone={summary.unassigned.count > 0 ? "attention" : "success"}
				/>
			</section>

			<section className="mb-6 rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
				<div className="flex items-start gap-4">
					<span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/10">
						<Landmark className="size-5" />
					</span>
					<div>
						<h2 className="font-semibold">Cheque primeiro, destino depois</h2>
						<p className="mt-1 max-w-4xl text-sm leading-6 text-emerald-50/75">
							O banco informa como o cheque foi liquidado; o destino confirmado informa para onde o
							valor foi. Somente “Caixa Amanda” ou “Caixa Sindi” cria a transferência interna. Um
							saque feito por terceiro continua sendo pagamento de frete de saída.
						</p>
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="flex flex-col justify-between gap-4 border-b border-stone-200 px-5 py-5 sm:flex-row sm:items-center">
					<div>
						<h2 className="font-semibold text-stone-950">Movimentos por data</h2>
						<p className="mt-1 text-xs text-stone-500">
							{visibleMovements.length} de {summary.movements.length} movimentos exibidos
						</p>
					</div>
					<div className="flex flex-wrap gap-1.5">
						<FilterButton active={filter === "ALL"} onClick={() => setFilter("ALL")}>
							Todos
						</FilterButton>
						{checkFilters.map((kind) => (
							<FilterButton key={kind} active={filter === kind} onClick={() => setFilter(kind)}>
								{checkKindPresentation[kind].label}
							</FilterButton>
						))}
					</div>
				</header>

				{groups.map((group) => (
					<section key={group.date}>
						<header className="flex items-center justify-between border-b border-stone-100 bg-stone-50/80 px-5 py-3">
							<div>
								<h3 className="text-sm font-semibold text-stone-800">
									{FinancePresentation.transactionDate(group.date)}
								</h3>
								<p className="mt-0.5 text-[11px] text-stone-500">
									{group.count} {group.count === 1 ? "movimento" : "movimentos"}
								</p>
							</div>
							<p className="text-sm font-semibold text-stone-900">
								{FinancePresentation.money(group.amountCents)}
							</p>
						</header>
						<div className="divide-y divide-stone-100">
							{group.items.map((movement) => (
								<CheckMovementRow
									key={movement.id}
									movement={movement}
									isSaving={
										destinationMutation.isPending &&
										destinationMutation.variables.checkNumber === movement.checkNumber
									}
									error={
										destinationMutation.variables?.checkNumber === movement.checkNumber
											? destinationMutation.error
											: null
									}
									onSave={(destinationId, cashAccountId, explanation) =>
										destinationMutation.mutate({
											checkNumber: movement.checkNumber,
											destinationId,
											cashAccountId,
											explanation,
										})
									}
								/>
							))}
						</div>
					</section>
				))}
				{groups.length === 0 && (
					<p className="px-6 py-12 text-center text-sm text-stone-500">
						Nenhum movimento de cheque neste filtro.
					</p>
				)}
			</section>
		</>
	);
}

function CheckMovementRow({
	movement,
	isSaving,
	error,
	onSave,
}: {
	movement: CheckMovement;
	isSaving: boolean;
	error: Error | null;
	onSave: (
		destinationId: CheckDestinationId,
		cashAccountId: "caixa-amanda" | "caixa-sindi" | null,
		explanation: string | null,
	) => void;
}) {
	const presentation = checkKindPresentation[movement.kind];
	const [destinationId, setDestinationId] = useState<CheckDestinationId | "">(
		movement.checkDestination?.destinationId ?? "",
	);
	const [explanation, setExplanation] = useState(
		movement.checkDestination?.destinationId === "OTHER"
			? movement.checkDestination.explanation
			: "",
	);
	const [cashAccountId, setCashAccountId] = useState<"caixa-amanda" | "caixa-sindi">(
		movement.checkDestination?.cashAccountId ?? "caixa-amanda",
	);

	useEffect(() => {
		setDestinationId(movement.checkDestination?.destinationId ?? "");
		setExplanation(
			movement.checkDestination?.destinationId === "OTHER"
				? movement.checkDestination.explanation
				: "",
		);
		setCashAccountId(movement.checkDestination?.cashAccountId ?? "caixa-amanda");
	}, [movement.checkDestination]);

	return (
		<article className="grid gap-4 px-5 py-4 transition hover:bg-stone-50/70 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(280px,1fr)_auto] lg:items-center">
			<div className="min-w-0">
				<p className="font-semibold text-stone-950">Cheque nº {movement.checkNumber}</p>
				<p className="mt-1 truncate text-xs text-stone-500">{movement.description}</p>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<span
					className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${presentation.className}`}
				>
					{presentation.icon}
					{presentation.label}
				</span>
				<ClassificationBadge movement={movement} />
				{movement.checkDestination?.destinationId === "OUTBOUND_FREIGHT" && (
					<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
						<CircleCheck className="size-3.5" />
						Conciliado
					</span>
				)}
			</div>

			{movement.kind === "RETURNED" ? (
				<p className="text-xs text-stone-500">Resolva a devolução antes de definir o destino.</p>
			) : (
				<div className="flex min-w-0 flex-col gap-2">
					<div className="flex gap-2">
						<select
							aria-label={`Destino do cheque ${movement.checkNumber}`}
							value={destinationId}
							onChange={(event) =>
								setDestinationId(CheckDestinations.fromFormValue(event.target.value))
							}
							className="h-10 min-w-0 flex-1 rounded-xl border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 outline-none focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
						>
							<option value="">Selecionar destino</option>
							{CHECK_DESTINATIONS.map((destination) => (
								<option key={destination.id} value={destination.id}>
									{destination.name}
								</option>
							))}
						</select>
						<button
							type="button"
							disabled={!destinationId || isSaving}
							onClick={() => {
								if (destinationId) {
									onSave(
										destinationId,
										destinationId === "CASH_ACCOUNT" ? cashAccountId : null,
										destinationId === "OTHER" ? explanation : null,
									);
								}
							}}
							className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-emerald-950 px-3 text-xs font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-40"
						>
							{isSaving ? (
								<LoaderCircle className="size-3.5 animate-spin" />
							) : (
								<Check className="size-3.5" />
							)}
							Confirmar
						</button>
					</div>
					{destinationId === "OTHER" && (
						<input
							value={explanation}
							onChange={(event) => setExplanation(event.target.value)}
							placeholder="Explique o destino"
							className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
						/>
					)}
					{destinationId === "CASH_ACCOUNT" && (
						<select
							aria-label={`Caixa que recebeu o cheque ${movement.checkNumber}`}
							value={cashAccountId}
							onChange={(event) =>
								setCashAccountId(
									event.target.value === "caixa-sindi" ? "caixa-sindi" : "caixa-amanda",
								)
							}
							className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100"
						>
							<option value="caixa-amanda">Caixa Amanda</option>
							<option value="caixa-sindi">Caixa Sindi</option>
						</select>
					)}
					{error && <p className="text-xs text-red-700">{error.message}</p>}
				</div>
			)}

			<p className="text-lg font-semibold tracking-[-0.03em] text-stone-950 sm:text-right">
				{FinancePresentation.money(movement.amountCents)}
			</p>
		</article>
	);
}

function ClassificationBadge({ movement }: { movement: CheckMovement }) {
	if (movement.checkDestination) {
		return (
			<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
				{movement.checkDestination.destination}
			</span>
		);
	}

	if (movement.classification) {
		return (
			<span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
				{movement.classification.destination}
			</span>
		);
	}

	if (movement.kind === "RETURNED") {
		return (
			<span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800">
				Revisar devolução
			</span>
		);
	}

	return (
		<span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
			Sem destino definido
		</span>
	);
}

function FilterButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-900"}`}
		>
			{children}
		</button>
	);
}
